import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Download, Loader2, Search, Filter } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRIES = [
  { key: 'retail', label: 'Retail / Supermercados' },
  { key: 'manufactura', label: 'Manufactura / Industria' },
  { key: 'tech', label: 'Tecnología / SaaS' },
  { key: 'salud', label: 'Salud / Clínicas' },
  { key: 'logistica', label: 'Logística / Transporte' },
  { key: 'alimentos', label: 'Alimentos y Bebidas' },
  { key: 'servicios', label: 'Servicios Profesionales' },
  { key: 'construccion', label: 'Construcción / Inmobiliaria' },
  { key: 'mineria', label: 'Minería' },
];

const INTERESTS = [
  { key: 'retail', label: 'Retail Tech' },
  { key: 'ia', label: 'IA' },
  { key: 'esl', label: 'ESL' },
  { key: 'logistica', label: 'Logística' },
  { key: 'sustentabilidad', label: 'Sustentabilidad' },
  { key: 'innovacion', label: 'Innovación' },
];

export default function NewsletterAdmin({ token }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [industry, setIndustry] = useState('all');
  const [preference, setPreference] = useState('all');
  const [q, setQ] = useState('');
  const [downloading, setDownloading] = useState(false);
  const client = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get('/admin/newsletter/subscribers');
      setSubs(data.subscribers || []);
    } catch (e) { toast.error('Error al cargar suscriptores'); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return subs.filter(s => {
      if (industry !== 'all' && s.industry !== industry) return false;
      if (preference !== 'all' && !(s.preferences || []).includes(preference)) return false;
      if (q) {
        const needle = q.toLowerCase();
        if (!(`${s.email} ${s.name || ''} ${s.whatsapp || ''}`).toLowerCase().includes(needle)) return false;
      }
      return true;
    });
  }, [subs, industry, preference, q]);

  const stats = useMemo(() => {
    const byIndustry = {};
    const byPref = {};
    let active = 0;
    subs.forEach(s => {
      if (s.active) active++;
      if (s.industry) byIndustry[s.industry] = (byIndustry[s.industry] || 0) + 1;
      (s.preferences || []).forEach(p => { byPref[p] = (byPref[p] || 0) + 1; });
    });
    return { total: subs.length, active, byIndustry, byPref };
  }, [subs]);

  const exportCSV = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (industry !== 'all') params.set('industry', industry);
      if (preference !== 'all') params.set('preference', preference);
      const url = `${API}/admin/newsletter/export${params.toString() ? '?' + params.toString() : ''}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Error ' + res.status);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `allcom_newsletter_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('CSV descargado');
    } catch (e) {
      toast.error('Error exportando CSV');
    }
    setDownloading(false);
  };

  return (
    <div data-testid="admin-newsletter">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2540] flex items-center gap-2" style={{ fontFamily: 'Syne,sans-serif' }}>
            <Mail className="w-6 h-6" />Suscriptores · Kian Daily
          </h1>
          <p className="text-sm text-slate-500 mt-1">{stats.total} suscriptores en total · {stats.active} activos</p>
        </div>
        <button
          onClick={exportCSV}
          disabled={downloading}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#0A2540] text-white text-sm font-semibold hover:bg-[#050E1F] disabled:opacity-50 shadow"
          data-testid="admin-newsletter-export"
        >
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Exportar CSV
        </button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="p-4 rounded-xl bg-white border border-slate-200"><div className="text-2xl font-bold text-[#0A2540]" style={{ fontFamily: 'Syne,sans-serif' }}>{stats.total}</div><div className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">Total</div></div>
        <div className="p-4 rounded-xl bg-white border border-slate-200"><div className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Syne,sans-serif' }}>{stats.active}</div><div className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">Activos</div></div>
        <div className="p-4 rounded-xl bg-white border border-slate-200"><div className="text-2xl font-bold text-[#0EA5E9]" style={{ fontFamily: 'Syne,sans-serif' }}>{Object.keys(stats.byIndustry).length}</div><div className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">Rubros cubiertos</div></div>
        <div className="p-4 rounded-xl bg-white border border-slate-200"><div className="text-2xl font-bold text-[#F8D90F]" style={{ fontFamily: 'Syne,sans-serif' }}>{subs.filter(s => s.whatsapp).length}</div><div className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">Con WhatsApp</div></div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-500"><Filter className="w-4 h-4" />Filtros:</div>
          <div className="flex-1 min-w-[180px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar email o nombre..." className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="admin-newsletter-search" />
          </div>
          <select value={industry} onChange={e => setIndustry(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="admin-newsletter-filter-industry">
            <option value="all">Todos los rubros</option>
            {INDUSTRIES.map(i => <option key={i.key} value={i.key}>{i.label}</option>)}
          </select>
          <select value={preference} onChange={e => setPreference(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="admin-newsletter-filter-preference">
            <option value="all">Todos los intereses</option>
            {INTERESTS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </div>
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#0EA5E9] mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 rounded-2xl bg-white border border-slate-200">
          <Mail className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">No hay suscriptores con estos filtros.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase">Email</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase">Nombre</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase">Rubro</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase">Intereses</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase">WhatsApp</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase">Freq.</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase">Creado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((s) => (
                  <tr key={s.id || s.email} className="hover:bg-slate-50" data-testid={`admin-newsletter-row-${s.email}`}>
                    <td className="px-4 py-3 font-mono text-xs">{s.email}</td>
                    <td className="px-4 py-3">{s.name || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3">{s.industry ? <span className="inline-block px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 text-[10px] font-bold uppercase">{s.industry}</span> : <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{(s.preferences || []).map(p => <span key={p} className="inline-block px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[9px] font-bold uppercase">{p}</span>)}{(s.preferences || []).length === 0 && <span className="text-slate-400">—</span>}</div></td>
                    <td className="px-4 py-3 text-xs">{s.whatsapp || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3 text-xs">{s.frequency || 'daily'}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{s.created_at ? new Date(s.created_at).toLocaleDateString('es-CL') : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 text-xs text-slate-500">
            Mostrando {filtered.length} de {subs.length} suscriptores
          </div>
        </div>
      )}
    </div>
  );
}
